/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import {
  ValidationError, PMSError, HttpError,
} from '@/errors';

const endpoint = `${apiEndpoint}/inventory/policies`;

const state = {
  data: null,
  pending: false,
  error: null,
  pmsError: null,
  updatePending: false,
  updateError: null,
  validationError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.data != null,
  cxlPols: (state, getters) => (getters.loaded ? state.data.cxlPols : []),
  cxlRules: (state, getters) => (getters.loaded ? state.data.cxlRules : []),
  pymtPols: (state, getters) => (getters.loaded ? state.data.pymtPols : []),
  bgarants: (state, getters) => (getters.loaded ? state.data.bgarants : []),
  pmts: (state, getters) => (getters.loaded ? state.data.pmts : []),
  validationError: (state) => state.validationError,
  policies: (state, getters) => (getters.loaded ? state.data : []),
};

const getSortRule = (a, b, order) => {
  const nameA = a.langs.en.name.toUpperCase(); // ignore upper and lowercase
  const nameB = b.langs.en.name.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return order ? -1 : 1;
  }
  if (nameA > nameB) {
    return order ? 1 : -1;
  }
  return 0;
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
    state.validationError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.data = null;
    }
    state.error = null;
    state.pending = true;
    state.validationError = null;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
    state.validationError = null;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
    state.pmsError = error;
  },
  data(state, data) {
    state.pending = false;
    state.data = data;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  deleted(state, payload = null) {
    if (payload != null) {
      let pols;
      const [id, type] = payload;
      if (type === 'payment') pols = state.data.pymtPols;
      if (type === 'cancel-rule') pols = state.data.cxlRules;
      if (type === 'cancel-policy') pols = state.data.cxlPols;
      const idx = pols.findIndex((pol) => Number(pol.id) === Number(id));
      if (idx !== -1) {
        pols.splice(idx, 1);
      }
    }
    state.updatePending = false;
    state.updateError = null;
  },
  validationError(state, error) {
    state.validationError = error;
    state.pending = false;
  },
  sortCxl(state, { field, order }) {
    if (field === 'langs.en.name') {
      state.data.cxlPols.sort((a, b) => getSortRule(a, b, order));
    }
    state.data.cxlPols.sort((a, b) => (order ? a[field] - b[field] : b[field] - a[field]));
  },
  sortPymt(state, { field, order }) {
    if (field === 'langs.en.name') {
      state.data.pymtPols.sort((a, b) => getSortRule(a, b, order));
    }
    state.data.pymtPols.sort((a, b) => (order ? a[field] - b[field] : b[field] - a[field]));
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async fetchData({ commit }, { currentLangCode = null, forced = false }) {
    commit('beforeLoading', forced);
    try {
      const response = await ApiService.get(`${endpoint}?currentLangCode=${currentLangCode}`);
      commit('data', response.data);
    } catch (error) {
      commit('error', error);
    }
  },
  async fetchDataV2({ commit }) {
    commit('beforeLoading');
    try {
      const response = await ApiService.get(`${endpoint}/v2`);
      commit('data', response.data);
    } catch (error) {
      commit('error', error);
    }
  },
  async createCxlRule({ commit, getters }, cxlPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/cancel-rule`, { ...cxlPol });
      const oldRules = getters.cxlRules.map(({ id }) => Number(id));
      commit('data', response.data);
      const newRules = getters.cxlRules.map(({ id }) => Number(id));
      commit('update');
      return newRules.find((id) => !oldRules.includes(id));
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateCxlRule({ commit }, cxlPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.put(`${endpoint}/cancel-rule/${cxlPol.id}`, { ...cxlPol });
      commit('data', response.data);
      commit('update');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteCxlRule({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { id, type } = payload;
      await ApiService.delete(`${endpoint}/cancel-rule/${id}`, { data: { type } });
      commit('deleted', [id, 'cancel-rule']);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async createPymtPol({ commit }, pymtPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/payment`, { ...pymtPol });
      commit('data', response.data);
      commit('update');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updatePymtPol({ commit }, pymtPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.put(`${endpoint}/payment/${pymtPol.id}`, { ...pymtPol });
      commit('data', response.data);
      commit('update');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deletePymtPol({ commit }, id) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/payment/${id}`);
      commit('deleted', [id, 'payment']);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async createCxlPol({ commit }, cxlPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/cancel-policy`, { ...cxlPol });
      commit('data', response.data);
      commit('update');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateCxlPol({ commit }, cxlPol) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.put(`${endpoint}/cancel-policy/${cxlPol.id}`, { ...cxlPol });
      commit('data', response.data);
      commit('update');
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteCxlPol({ commit }, id) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/cancel-policy/${id}`);
      commit('deleted', [id, 'cancel-policy']);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async generateCxlText({ commit }, payload) {
    try {
      const { data } = await ApiService.post(`${endpoint}/cancel-rule/text`, { ...payload });
      return data;
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
